import { useEffect, useState, type FormEvent } from "react";
import { AlertCircle, BadgePercent, History } from "lucide-react";
import {
  getCsrfToken,
  getCustomerFeeOverride,
  getFeeSettings,
  updateCustomerFeeOverride
} from "../lib/api";
import { startGoogleSession } from "../lib/auth";
import {
  EXAMPLE_AMOUNT_CENTS,
  commissionCents,
  describePricing,
  fixedFeeToInput,
  formatFee,
  formatUsd,
  parseFixedFee,
  parsePercent,
  rateToPercent
} from "../lib/feeMath";
import { useTimeZone } from "../lib/TimeZoneContext";
import { formatTimestamp } from "../lib/timeZone";
import { GuardedChangeForm } from "./GuardedChangeForm";
import { Skeleton } from "./Skeleton";
import type { CustomerFeeOverrideBundle, FeeSettings, Viewer } from "../types";

/**
 * A history row in words, where either component may have been left inheriting.
 * `describePricing` cannot do this: it takes a resolved percentage, and an
 * inherited component has no number of its own to show.
 */
function describeHistoryPricing(rate: string | null, fixedCents: number | null): string {
  if (rate === null && fixedCents === null) return "Platform pricing";
  const percentPart = rate === null ? "platform rate" : `${rateToPercent(rate).percentDisplay}%`;
  const feePart = fixedCents === null
    ? "platform per-card fee"
    : fixedCents === 0 ? "no per-card fee" : `${formatUsd(fixedCents)} per card`;
  return `${percentPart} + ${feePart}`;
}

/** Which of the three things an operator is actually trying to say. */
type PricingMode = "platform" | "free" | "custom";

interface CustomerPricingPanelProps {
  customerGuid: string;
  viewer: Viewer;
}

/**
 * What this customer pays, and the control to change it.
 *
 * The panel always leads with the *effective* price rather than the override,
 * because half of it may be inherited and an operator acting on a half-answer
 * reprices a real person.
 */
export function CustomerPricingPanel({ customerGuid, viewer }: CustomerPricingPanelProps) {
  const { timeZone } = useTimeZone();
  const [bundle, setBundle] = useState<CustomerFeeOverrideBundle | null>(null);
  const [platform, setPlatform] = useState<FeeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // The API may not carry per-customer pricing yet. That is a deploy-window state,
  // not a fault, and it should not put a red error on every customer profile.
  const [unsupported, setUnsupported] = useState(false);

  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<PricingMode>("custom");
  const [followPlatformRate, setFollowPlatformRate] = useState(false);
  const [followPlatformFee, setFollowPlatformFee] = useState(false);
  const [percentInput, setPercentInput] = useState("");
  const [feeInput, setFeeInput] = useState("");
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [override, settings] = await Promise.all([
        getCustomerFeeOverride(customerGuid),
        getFeeSettings()
      ]);
      setBundle(override);
      setPlatform(settings.current);
    } catch (loadError) {
      if ((loadError as { status?: number }).status === 404) {
        setUnsupported(true);
        return;
      }
      setError(loadError instanceof Error ? loadError.message : "Unable to load customer pricing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setUnsupported(false);
    void load();
    setEditing(false);
    // Reloading for a different customer must not carry the previous one's draft.
  }, [customerGuid]);

  const current = bundle?.current ?? null;
  const currentPercent = current ? rateToPercent(current.effectiveCommissionRate) : null;
  const currentFee = currentPercent && current
    ? commissionCents(EXAMPLE_AMOUNT_CENTS, currentPercent.milliPercent, current.effectiveFixedFeeCents)
    : 0;
  const isCustom = current ? current.rateSource === "customer" || current.fixedFeeSource === "customer" : false;

  // What the form is proposing, resolved the same way the server will resolve it.
  const proposedPercent = mode === "free"
    ? parsePercent("0", true)
    : mode === "platform" || followPlatformRate
      ? (platform ? rateToPercent(platform.commissionRate) : null)
      : parsePercent(percentInput, true);
  const proposedFeeCents = mode === "free"
    ? 0
    : mode === "platform" || followPlatformFee
      ? platform?.fixedFeeCents ?? null
      : parseFixedFee(feeInput)?.cents ?? null;
  const proposedTotal = proposedPercent && proposedFeeCents !== null
    ? commissionCents(EXAMPLE_AMOUNT_CENTS, proposedPercent.milliPercent, proposedFeeCents)
    : null;

  const proposedDescription = mode === "platform"
    ? "platform pricing"
    : proposedPercent && proposedFeeCents !== null
      ? describePricing(proposedPercent.percentDisplay, proposedFeeCents)
      : "";
  // The phrase an operator has to type back. "PLATFORM PRICING" for the reset,
  // because "2.9% + $0.49 per card" would then be ambiguous with setting those
  // same numbers as this customer's own pinned price.
  const confirmPhrase = mode === "platform" ? "PLATFORM PRICING" : proposedDescription;
  const inputsValid = mode === "platform" || (proposedPercent !== null && proposedFeeCents !== null);

  const openEditor = () => {
    if (!current || !platform) return;
    const startAsFree = current.rateSource === "customer" && current.commissionRate === "0.00000"
      && current.fixedFeeSource === "customer" && current.fixedFeeCents === 0;
    setMode(startAsFree ? "free" : isCustom ? "custom" : "platform");
    setFollowPlatformRate(current.commissionRate === null);
    setFollowPlatformFee(current.fixedFeeCents === null);
    setPercentInput(rateToPercent(current.effectiveCommissionRate).percentDisplay);
    setFeeInput(fixedFeeToInput(current.effectiveFixedFeeCents));
    setReason("");
    setConfirmation("");
    setSaveError(null);
    setEditing(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!current || !inputsValid) return;
    let submitted = false;
    setSaving(true);
    setSaveError(null);
    try {
      await startGoogleSession();
      const csrfToken = await getCsrfToken();
      submitted = true;
      const updated = await updateCustomerFeeOverride(
        customerGuid,
        {
          commissionRate: mode === "platform" || (mode === "custom" && followPlatformRate)
            ? null
            : proposedPercent?.rate ?? null,
          fixedFeeCents: mode === "platform" || (mode === "custom" && followPlatformFee)
            ? null
            : proposedFeeCents,
          expectedVersion: current.version,
          operationId: crypto.randomUUID(),
          reason: reason.trim(),
          csrfToken
        },
        viewer.email
      );
      setBundle(updated);
      setEditing(false);
    } catch (saveFailure) {
      if (!submitted) {
        setSaveError("Fresh Google authentication is required to change pricing. Your entries have been preserved.");
        return;
      }
      const status = (saveFailure as { status?: number }).status;
      if (status === 409) {
        setSaveError("Another admin changed this customer's pricing. The current values have been refreshed — review them before saving.");
      } else {
        setSaveError("The save result could not be confirmed. The current values have been refreshed; review them and the history before retrying.");
      }
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (unsupported) return null;

  return (
    <section className="history-card customer-pricing-card" aria-busy={loading}>
      <div className="section-heading-row">
        <div>
          <p className="section-label">PRICING</p>
          <h2>What this customer pays</h2>
        </div>
        {isCustom ? <span className="status-badge status-review">custom pricing</span> : null}
      </div>

      {error ? (
        <p className="guarded-change-error" role="alert"><AlertCircle size={16} aria-hidden="true" />{error}</p>
      ) : null}

      {loading || !current || !currentPercent ? (
        <Skeleton width="220px" height="30px" />
      ) : (
        <>
          <p className="customer-pricing-value">
            <BadgePercent size={19} aria-hidden="true" />
            <strong>{describePricing(currentPercent.percentDisplay, current.effectiveFixedFeeCents)}</strong>
          </p>
          <dl className="customer-pricing-sources">
            <div>
              <dt>Percentage</dt>
              <dd>{current.rateSource === "customer" ? "Set for this customer" : "Follows platform"}</dd>
            </div>
            <div>
              <dt>Per card</dt>
              <dd>{current.fixedFeeSource === "customer" ? "Set for this customer" : "Follows platform"}</dd>
            </div>
            <div>
              <dt>On a $100.00 gift</dt>
              <dd>{formatFee(currentFee)}</dd>
            </div>
          </dl>

          {viewer.role === "superAdmin" && !editing ? (
            <button className="secondary-button full-button" onClick={openEditor} disabled={!platform}>
              {isCustom ? "Change this customer's pricing" : "Set custom pricing"}
            </button>
          ) : null}
          {viewer.role !== "superAdmin" ? (
            <p className="field-help">Only super-admins can change what a customer pays.</p>
          ) : null}
        </>
      )}

      {editing && current ? (
        <GuardedChangeForm
          title={mode === "platform" ? "Return this customer to platform pricing" : "Change this customer's pricing"}
          impact={proposedTotal === null ? (
            <>Enter a percentage and a per-card fee to see what this customer would pay.</>
          ) : (
            <>
              Every new gift this customer sends will be charged <strong>{proposedDescription}</strong> —
              {" "}{formatFee(proposedTotal)} on a $100.00 gift, against {formatFee(currentFee)} today.
              Gifts already in flight keep their original fee.
            </>
          )}
          phrase={confirmPhrase}
          reason={reason}
          onReasonChange={setReason}
          reasonPlaceholder="Why is this customer priced differently?"
          confirmation={confirmation}
          onConfirmationChange={setConfirmation}
          requirements={[{ label: "Pricing is complete and in range", met: inputsValid }]}
          submitLabel={saving ? "Saving…" : "Apply pricing"}
          saving={saving}
          error={saveError}
          onCancel={() => setEditing(false)}
          onSubmit={(event) => void submit(event)}
        >
          <fieldset className="pricing-mode">
            <legend>Pricing for this customer</legend>
            {([
              ["platform", "Platform pricing", "Follows whatever the platform charges, now and after it changes."],
              ["free", "No fee at all", "0% and no per-card fee. This customer sends gifts for free."],
              ["custom", "Custom pricing", "Set either component; leave the other following the platform."]
            ] as Array<[PricingMode, string, string]>).map(([value, label, hint]) => (
              <label key={value} className={mode === value ? "is-selected" : undefined}>
                <input
                  type="radio"
                  name="pricing-mode"
                  checked={mode === value}
                  onChange={() => { setMode(value); setConfirmation(""); }}
                />
                <span><strong>{label}</strong><small>{hint}</small></span>
              </label>
            ))}
          </fieldset>

          {mode === "custom" ? (
            <div className="pricing-inputs">
              <label>
                <span>Percentage</span>
                <span className="percent-input">
                  <input
                    inputMode="decimal"
                    value={followPlatformRate ? rateToPercent(platform?.commissionRate ?? "0.00000").percentDisplay : percentInput}
                    disabled={followPlatformRate}
                    onChange={(event) => { setPercentInput(event.target.value); setConfirmation(""); }}
                  />
                  <b>%</b>
                </span>
                <small className="follow-platform">
                  <input
                    type="checkbox"
                    checked={followPlatformRate}
                    onChange={(event) => { setFollowPlatformRate(event.target.checked); setConfirmation(""); }}
                  />
                  Follow platform
                </small>
              </label>
              <label>
                <span>Per card</span>
                <span className="percent-input is-currency">
                  <b>$</b>
                  <input
                    inputMode="decimal"
                    value={followPlatformFee ? fixedFeeToInput(platform?.fixedFeeCents ?? 0) : feeInput}
                    disabled={followPlatformFee}
                    onChange={(event) => { setFeeInput(event.target.value); setConfirmation(""); }}
                  />
                </span>
                <small className="follow-platform">
                  <input
                    type="checkbox"
                    checked={followPlatformFee}
                    onChange={(event) => { setFollowPlatformFee(event.target.checked); setConfirmation(""); }}
                  />
                  Follow platform
                </small>
              </label>
            </div>
          ) : null}

          <div className="impact-panel">
            <p className="section-label">EFFECT ON A $100.00 GIFT</p>
            <div className="impact-row">
              <span>Fee</span>
              <span>{formatFee(currentFee)} → <strong>{proposedTotal === null ? "—" : formatFee(proposedTotal)}</strong></span>
            </div>
            <div className="impact-row">
              <span>Sender pays</span>
              <span>
                {formatUsd(EXAMPLE_AMOUNT_CENTS + currentFee)} →{" "}
                <strong>{proposedTotal === null ? "—" : formatUsd(EXAMPLE_AMOUNT_CENTS + proposedTotal)}</strong>
              </span>
            </div>
          </div>
        </GuardedChangeForm>
      ) : null}

      {bundle && bundle.history.length > 0 ? (
        <div className="customer-pricing-history">
          <p className="section-label"><History size={14} aria-hidden="true" /> PRICING HISTORY</p>
          <ul>
            {bundle.history.map((entry) => (
              <li key={`${entry.at}-${entry.actorEmail}`}>
                <strong>{describeHistoryPricing(entry.newRate, entry.newFixedFeeCents)}</strong>
                <span>{formatTimestamp(entry.at, timeZone)} · {entry.actorEmail}</span>
                <em>“{entry.reason}”</em>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
